import { useEffect, useState } from 'react';
import { Plus, Send, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { config } from '../../config';
import { useAdminHeaders } from '../../auth/useAdminHeaders';

interface Incident {
  id: string;
  title: string;
  summary: string;
  severity: string;
  state: string;
  started_at: string;
  resolved_at?: string;
}

interface Component {
  id: string;
  display_name: string;
  status: string;
}

export default function IncidentsAdmin() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    severity: 'MINOR',
    state: 'INVESTIGATING',
    componentIds: [] as string[],
  });
  const [updateData, setUpdateData] = useState({
    state: 'INVESTIGATING',
    message: '',
  });

  const apiUrl = config.apiUrl;
  const internalApiUrl = config.internalApiUrl;
  const getHeaders = useAdminHeaders();

  useEffect(() => {
    fetchIncidents();
    fetchComponents();
  }, []);

  async function fetchIncidents() {
    try {
      const response = await fetch(`${apiUrl}/v1/stats/incidents?limit=50`);
      if (response.ok) {
        const data = await response.json();
        setIncidents(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch incidents', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchComponents() {
    try {
      const response = await fetch(`${apiUrl}/v1/stats/components`);
      if (response.ok) {
        const data = await response.json();
        setComponents(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch components', error);
    }
  }

  async function handleCreate() {
    if (formData.componentIds.length === 0) {
      alert('Please select at least one affected component');
      return;
    }

    try {
      const response = await fetch(`${internalApiUrl}/api-internal/v1/stats/incidents`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await fetchIncidents();
        setShowCreateForm(false);
        setFormData({
          title: '',
          summary: '',
          severity: 'MINOR',
          state: 'INVESTIGATING',
          componentIds: [],
        });
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to create incident');
      }
    } catch (error) {
      console.error('Failed to create incident', error);
      alert('Failed to create incident');
    }
  }

  async function handleUpdate(incidentId: string) {
    try {
      const response = await fetch(`${internalApiUrl}/api-internal/v1/stats/incidents/${incidentId}/updates`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        await fetchIncidents();
        setShowUpdateForm(null);
        setUpdateData({ state: 'INVESTIGATING', message: '' });
      }
    } catch (error) {
      console.error('Failed to update incident', error);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Are you sure you want to delete this incident? This will also delete all associated updates.')) return;

    try {
      const response = await fetch(`${internalApiUrl}/api-internal/v1/stats/incidents/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (response.ok) {
        fetchIncidents();
      }
    } catch (error) {
      console.error('Failed to delete incident', error);
    }
  }

  const severityOptions = [
    { value: 'MINOR', label: 'Minor' },
    { value: 'MAJOR', label: 'Major' },
    { value: 'CRITICAL', label: 'Critical' },
  ];

  const stateOptions = [
    { value: 'INVESTIGATING', label: 'Investigating' },
    { value: 'IDENTIFIED', label: 'Identified' },
    { value: 'MONITORING', label: 'Monitoring' },
    { value: 'RESOLVED', label: 'Resolved' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="relative">
          <div className="w-10 h-10 border-2 border-gray-200 rounded-full" />
          <div className="absolute inset-0 w-10 h-10 border-2 border-transparent border-t-unlimit-lime rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 bg-unlimit-gray-light border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-unlimit-lime/50 focus:border-unlimit-lime outline-none transition-all";

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-bold text-navy-900">Incident Management</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-rose-400 text-white font-semibold text-sm rounded-lg hover:bg-rose-500 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Create Incident</span>
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-card border border-gray-100 p-5 mb-5 animate-slide-down">
          <h3 className="text-sm font-bold text-navy-900 mb-4">Create New Incident</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={inputClass}
                placeholder="Brief description of the incident"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Summary</label>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                className={inputClass}
                rows={3}
                placeholder="Detailed description of the incident"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Affected Components <span className="text-rose-300">*</span>
              </label>
              <div className="border border-gray-200 rounded-lg p-2 max-h-40 overflow-y-auto bg-unlimit-gray-light">
                {components.length === 0 ? (
                  <p className="text-gray-400 text-xs p-2">Loading components...</p>
                ) : (
                  components.map((component) => (
                    <label key={component.id} className="flex items-center space-x-2 py-1.5 px-2 rounded-md cursor-pointer hover:bg-white transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.componentIds.includes(component.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, componentIds: [...formData.componentIds, component.id] });
                          } else {
                            setFormData({ ...formData, componentIds: formData.componentIds.filter(id => id !== component.id) });
                          }
                        }}
                        className="rounded border-gray-300 text-unlimit-lime focus:ring-unlimit-lime"
                      />
                      <span className="text-sm text-navy-900">{component.display_name}</span>
                    </label>
                  ))
                )}
              </div>
              {formData.componentIds.length === 0 && (
                <p className="text-rose-300 text-[11px] mt-1">At least one component must be selected</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Severity</label>
                <select
                  value={formData.severity}
                  onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                  className={`${inputClass} appearance-none`}
                >
                  {severityOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">State</label>
                <select
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className={`${inputClass} appearance-none`}
                >
                  {stateOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              className="px-4 py-2 text-sm bg-rose-400 text-white font-semibold rounded-lg hover:bg-rose-500 transition-colors"
            >
              Create Incident
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {incidents.map((incident) => (
          <div key={incident.id} className="bg-white rounded-xl shadow-card border border-gray-100 p-5">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <div className="flex items-center flex-wrap gap-2 mb-1.5">
                  <h3 className="text-sm font-bold text-navy-900">{incident.title}</h3>
                  <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-md ${
                    incident.severity === 'CRITICAL' ? 'bg-rose-50 text-rose-300' :
                    incident.severity === 'MAJOR' ? 'bg-amber-50 text-amber-500' :
                    'bg-yellow-50 text-yellow-700'
                  }`}>
                    {incident.severity}
                  </span>
                  <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-md ${
                    incident.state === 'RESOLVED' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'
                  }`}>
                    {incident.state}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-1.5">{incident.summary}</p>
                <p className="text-[11px] text-gray-400">
                  Started: {format(new Date(incident.started_at), 'PPpp')}
                  {incident.resolved_at && ` \u00b7 Resolved: ${format(new Date(incident.resolved_at), 'PPpp')}`}
                </p>
              </div>
              <div className="flex items-center space-x-1 ml-4">
                {incident.state !== 'RESOLVED' && (
                  <button
                    onClick={() => setShowUpdateForm(incident.id)}
                    className="flex items-center space-x-1.5 px-3 py-1.5 bg-navy-900 text-white rounded-md hover:bg-navy-800 text-xs font-medium transition-colors"
                  >
                    <Send className="h-3 w-3" />
                    <span>Update</span>
                  </button>
                )}
                <button
                  onClick={() => handleDelete(incident.id)}
                  className="p-1.5 text-gray-400 hover:text-rose-400 hover:bg-rose-50 rounded-md transition-colors"
                  title="Delete incident"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {showUpdateForm === incident.id && (
              <div className="mt-4 pt-4 border-t border-gray-100 animate-slide-down">
                <h4 className="text-xs font-bold text-navy-900 mb-3">Post Incident Update</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">New State</label>
                    <select
                      value={updateData.state}
                      onChange={(e) => setUpdateData({ ...updateData, state: e.target.value })}
                      className={`${inputClass} appearance-none`}
                    >
                      {stateOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Update Message</label>
                    <textarea
                      value={updateData.message}
                      onChange={(e) => setUpdateData({ ...updateData, message: e.target.value })}
                      className={inputClass}
                      rows={3}
                      placeholder="Describe the current status and any actions taken..."
                    />
                  </div>
                </div>
                <div className="mt-3 flex justify-end space-x-2">
                  <button
                    onClick={() => setShowUpdateForm(null)}
                    className="px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleUpdate(incident.id)}
                    className="px-3 py-1.5 text-xs bg-navy-900 text-white font-semibold rounded-md hover:bg-navy-800 transition-colors"
                  >
                    Post Update
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {incidents.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-sm font-medium text-gray-500">No incidents found</p>
            <p className="text-xs mt-1">Create one to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}
